"use client"
import { useParams, useNavigate } from "react-router-dom"
import { FiTruck, FiAlertCircle, FiPhone, FiMail, FiMapPin, FiClock, FiCheckCircle, FiArrowRight } from "react-icons/fi"
import { useState } from "react"
import { useMoveData } from "../../hooks/useMoveData"
import NotificationSystem from "./MoveDetails/NotificationSystem"
import DocumentUpload from "./MoveDetails/DocumentUpload"
import LiveChat from "./MoveDetails/LiveChat"
import RatingSystem from "./MoveDetails/RatingSystem"

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: {
      label: "En attente",
      className: "bg-amber-500/20 text-amber-300 border-amber-500/30",
      icon: <FiClock className="w-3 h-3" />,
    },
    confirmed: {
      label: "Confirmé",
      className: "bg-blue-500/20 text-blue-300 border-blue-500/30",
      icon: <FiCheckCircle className="w-3 h-3" />,
    },
    in_progress: {
      label: "En cours",
      className: "bg-amber-400/20 text-amber-300 border-amber-400/30",
      icon: <FiTruck className="w-3 h-3" />,
    },
    completed: {
      label: "Terminé",
      className: "bg-green-500/20 text-green-300 border-green-500/30",
      icon: <FiCheckCircle className="w-3 h-3" />,
    },
    cancelled: {
      label: "Annulé",
      className: "bg-red-500/20 text-red-300 border-red-500/30",
      icon: <FiAlertCircle className="w-3 h-3" />,
    },
  }

  const config = statusConfig[status] || {
    label: status,
    className: "bg-gray-500/20 text-gray-300 border-gray-500/30",
    icon: <FiAlertCircle className="w-3 h-3" />,
  }

  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-300 hover:scale-105 ${config.className}`}
    >
      {config.icon}
      {config.label}
    </span>
  )
}

const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-900 flex items-center justify-center">
    <div className="text-center space-y-6">
      <div className="relative">
        <div className="animate-spin rounded-full h-20 w-20 border-4 border-gray-700 border-t-amber-400"></div>
        <div
          className="absolute inset-0 rounded-full h-20 w-20 border-4 border-transparent border-t-amber-300 animate-spin"
          style={{ animationDelay: "0.1s", animationDuration: "1.5s" }}
        ></div>
      </div>
      <div className="space-y-2">
        <p className="text-gray-300 text-lg font-medium">Chargement des détails</p>
        <p className="text-gray-500 text-sm">Veuillez patienter...</p>
      </div>
    </div>
  </div>
)

const ErrorDisplay = ({ error }) => (
  <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
    <div className="max-w-md w-full">
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center backdrop-blur-sm">
        <FiAlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
        <h2 className="text-2xl font-bold text-red-300 mb-3">Erreur de chargement</h2>
        <p className="text-red-200/80 mb-6">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all duration-200 border border-red-500/30 hover:border-red-500/50"
        >
          Réessayer
        </button>
      </div>
    </div>
  </div>
)

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return ""
  const cleaned = phoneNumber.replace(/\D/g, "")
  if (cleaned.length === 8) {
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})/, "$1 $2 $3")
  } else if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{2})(\d{3})(\d{3})/, "+$1 $2 $3 $4")
  } else if (cleaned.length === 12) {
    return cleaned.replace(/(\d{3})(\d{2})(\d{3})(\d{2})(\d{2})/, "+$1 $2 $3 $4 $5")
  }
  return phoneNumber
}

const ProgressTimeline = ({ moveData }) => {
  const timelineSteps = [
    {
      key: "created",
      title: "Devis créé",
      description: "Votre demande a été enregistrée",
      icon: <FiMail className="w-5 h-5" />,
      bgColor: "bg-amber-400",
    },
    {
      key: "confirmed",
      title: "Devis accepté",
      description: "Le déménagement est confirmé",
      icon: <FiCheckCircle className="w-5 h-5" />,
      bgColor: "bg-amber-500",
    },
    {
      key: "in_progress",
      title: "Déménagement en cours",
      description: "L'équipe est en route",
      icon: <FiTruck className="w-5 h-5" />,
      bgColor: "bg-amber-400",
    },
    {
      key: "completed",
      title: "Déménagement terminé",
      description: "Mission accomplie avec succès",
      icon: <FiCheckCircle className="w-5 h-5" />,
      bgColor: "bg-green-500",
    },
  ]

  const getCurrentStepIndex = () => {
    switch (moveData?.status) {
      case "pending":
        return 0
      case "confirmed":
        return 1
      case "in_progress":
        return 2
      case "completed":
        return 3
      default:
        return 0
    }
  }

  const currentStepIndex = getCurrentStepIndex()

  const getStepDate = (step, index) => {
    if (index > currentStepIndex) return null

    switch (index) {
      case 0:
        return moveData?.created_at
      case 1:
        return moveData?.updated_at
      case 2:
        return moveData?.started_at
      case 3:
        return moveData?.completed_at
      default:
        return null
    }
  }

  return (
    <div className="bg-gray-800 rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden">
      <div className="px-6 py-5 bg-gradient-to-r from-amber-500/10 to-amber-400/10 border-b border-gray-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-400/20 flex items-center justify-center">
            <FiTruck className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <div className="text-sm text-gray-400">Suivi du déménagement</div>
            <div className="text-lg font-semibold text-white">Progression en temps réel</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="relative">
          {timelineSteps.map((step, index) => {
            const isActive = index <= currentStepIndex
            const stepDate = getStepDate(step, index)

            return (
              <div key={step.key} className="flex items-start mb-8 last:mb-0 group">
                <div className="relative flex-shrink-0">
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all duration-300 ${
                      isActive
                        ? `${step.bgColor} border-white text-white shadow-lg scale-110`
                        : "bg-gray-700/50 border-gray-600 text-gray-400"
                    }`}
                  >
                    {step.icon}
                  </div>
                  {index < timelineSteps.length - 1 && (
                    <div
                      className={`absolute top-12 left-1/2 transform -translate-x-1/2 w-0.5 h-12 transition-all duration-500 ${
                        index < currentStepIndex ? "bg-amber-400" : "bg-gray-700"
                      }`}
                    />
                  )}
                </div>

                <div className="ml-6 flex-1 pb-8">
                  <div
                    className={`font-semibold text-lg transition-colors duration-200 ${
                      isActive ? "text-white" : "text-gray-400"
                    }`}
                  >
                    {step.title}
                  </div>
                  <div
                    className={`text-sm mt-1 transition-colors duration-200 ${
                      isActive ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    {step.description}
                  </div>
                  {isActive && stepDate && (
                    <div className="text-xs text-amber-400 mt-2 font-medium">
                      {new Date(stepDate).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const MoveInfoCard = ({ moveData }) => (
  <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700/50 overflow-hidden">
    <div className="px-6 py-5 bg-gradient-to-r from-amber-500/10 to-amber-400/10 border-b border-gray-700/50">
      <div className="flex items-center gap-3">
        <FiMapPin className="w-5 h-5 text-amber-400" />
        <div className="text-lg font-semibold text-white">Informations du déménagement</div>
      </div>
    </div>

    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between py-3 border-b border-gray-700/30">
        <span className="font-medium text-white">Statut:</span>
        <StatusBadge status={moveData.status} />
      </div>

      <div className="flex items-center justify-between py-3 border-b border-gray-700/30">
        <span className="font-medium text-white">Référence:</span>
        <span className="text-gray-300 font-mono text-sm bg-gray-700/30 px-3 py-1 rounded-lg">{moveData.id}</span>
      </div>

      {typeof moveData.price_cents === "number" && (
        <div className="flex items-center justify-between py-3 border-b border-gray-700/30">
          <span className="font-medium text-white">Prix:</span>
          <span className="text-amber-400 font-bold text-lg">{(moveData.price_cents / 100).toFixed(2)} TND</span>
        </div>
      )}

      {(moveData.first_name || moveData.last_name || moveData.company_name) && (
        <div className="flex items-center justify-between py-3">
          <span className="font-medium text-white">Déménageur:</span>
          <span className="text-gray-300 font-medium">
            {moveData.company_name || `${moveData.first_name || ""} ${moveData.last_name || ""}`.trim()}
          </span>
        </div>
      )}
    </div>
  </div>
)

const ContactCard = ({ moveData }) => {
  const moverName =
    moveData.company_name || `${moveData.first_name || ""} ${moveData.last_name || ""}`.trim() || "Déménageur Liberty"

  return (
    <div className="bg-gray-800 rounded-2xl shadow-xl border border-gray-700/50 overflow-hidden">
      <div className="px-6 py-5 bg-gradient-to-r from-amber-500/10 to-amber-400/10 border-b border-gray-700/50">
        <div className="flex items-center gap-3">
          <FiPhone className="w-5 h-5 text-amber-400" />
          <div className="text-lg font-semibold text-white">Contact rapide</div>
        </div>
      </div>

      <div className="p-6">
        {/* Mover Profile */}
        <div className="flex items-center gap-4 mb-6 p-4 bg-gray-700/30 rounded-xl border border-gray-600/30">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-400/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <div className="font-semibold text-white text-lg">{moverName}</div>
            <div className="text-sm text-gray-400">Votre déménageur professionnel</div>
            {moveData.email && (
              <div className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <FiMail className="w-3 h-3" />
                {moveData.email}
              </div>
            )}
          </div>
        </div>

        {/* Contact Options */}
        <div className="space-y-3">
          {moveData.phone_number ? (
            <>
              {/* WhatsApp */}
              <a
                href={`https://wa.me/${moveData.phone_number.replace(/\D/g, "")}?text=Bonjour%2C%20je%20vous%20contacte%20concernant%20le%20d%C3%A9m%C3%A9nagement%20${moveData.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between px-4 py-4 bg-green-500/10 hover:bg-green-500/20 rounded-xl text-green-400 transition-all duration-200 border border-green-500/30 hover:border-green-500/50 hover:shadow-lg hover:shadow-green-500/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold">WhatsApp</div>
                    <div className="text-xs text-green-300">Réponse rapide garantie</div>
                  </div>
                </div>
                <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>

              {/* Phone */}
              <a
                href={`tel:${moveData.phone_number}`}
                className="group flex items-center justify-between px-4 py-4 bg-amber-400/10 hover:bg-amber-400/20 rounded-xl text-amber-400 transition-all duration-200 border border-amber-400/30 hover:border-amber-400/50 hover:shadow-lg hover:shadow-amber-400/10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-400/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <FiPhone className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold">Appeler maintenant</div>
                    <div className="text-xs text-amber-300">Disponible 24h/24</div>
                  </div>
                </div>
                <span className="text-sm font-mono bg-amber-400/20 px-3 py-1 rounded text-amber-300">
                  {formatPhoneNumber(moveData.phone_number)}
                </span>
              </a>
            </>
          ) : (
            <div className="text-center py-6 text-gray-400 bg-gray-700/20 rounded-xl border border-gray-600/30">
              <FiPhone className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Aucun numéro de téléphone disponible</p>
            </div>
          )}

          {/* Email */}
          {moveData.email && (
            <a
              href={`mailto:${moveData.email}?subject=Demande d'information - Déménagement ${moveData.id}`}
              className="group flex items-center justify-between px-4 py-4 bg-gray-700/30 hover:bg-gray-700/50 rounded-xl text-gray-300 hover:text-white transition-all duration-200 border border-gray-600/30 hover:border-gray-500/50"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-600/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FiMail className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold">Envoyer un email</div>
                  <div className="text-xs text-gray-400">{moveData.email}</div>
                </div>
              </div>
              <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default function ClientMoveDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { moveData, isLoading, error, refetch } = useMoveData(id)
  const [notifications, setNotifications] = useState([])
  const addNotification = (notification) => {
    const id = Date.now()
    const newNotification = { id, ...notification }
    setNotifications((prev) => [...prev, newNotification])
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    }, 5000)
  }
  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }

  const handleUploadSuccess = () => {
    addNotification({
      type: "success",
      title: "Documents téléchargés",
      message: "Vos fichiers ont été ajoutés avec succès",
    })
    refetch()
  }

  const handleRatingSubmit = (ratingData) => {
    addNotification({
      type: "success",
      title: "Évaluation envoyée",
      message: "Merci pour votre retour !",
    })
  }

  // Loading state
  if (isLoading) return <LoadingSpinner />

  // Error state
  if (error) return <ErrorDisplay error={error} />

  // No data state
  if (!moveData) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8 text-center">
            <FiAlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-2xl font-bold text-gray-300 mb-3">Aucune donnée</h2>
            <p className="text-gray-400">Aucune donnée disponible pour ce déménagement</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <NotificationSystem notifications={notifications} removeNotification={removeNotification} />

      {/* Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 border-b border-gray-700/50">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-amber-400/5"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-amber-500 rounded-2xl flex items-center justify-center shadow-lg">
                  <FiTruck className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white text-balance">Détails du déménagement</h1>
                  <p className="text-gray-400 text-lg">
                    Référence: <span className="text-amber-400 font-mono">{moveData.id}</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={moveData.status} />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Timeline Section */}
        <ProgressTimeline moveData={moveData} />

        {/* Info and Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <MoveInfoCard moveData={moveData} />
          <ContactCard moveData={moveData} />
        </div>

        {/* Document Upload Section */}
        <DocumentUpload moveId={moveData.id} onUploadSuccess={handleUploadSuccess} />

        {/* Rating System - Show only if move is completed */}
        {moveData.status === "completed" && <RatingSystem moveId={moveData.id} onRatingSubmit={handleRatingSubmit} />}
      </div>

      <LiveChat
        moveId={moveData.id}
        moverName={
          moveData.company_name ||
          `${moveData.first_name || ""} ${moveData.last_name || ""}`.trim() ||
          "Déménageur Liberty"
        }
      />
    </div>
  )
}
